import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Button, Card, PageHeader, Section, Stat } from '@/components/ui';
import { MermaidDiagram } from '@/components/charts';
import { formatGain, summaryGains } from '@/content/benchmarks';
import type { BenchmarkPlatform } from '@/content/benchmarks';
import { SITE } from '@/lib/site';
import { LevelResults, RuledList } from './BenchmarkBlocks';

interface BenchmarkDetailProps {
  platform: BenchmarkPlatform;
  /** The other family, linked at the foot of the page. */
  other: BenchmarkPlatform;
  /** Extra material under the end-to-end results (charts, the control-arm argument). */
  e2eExtra?: ReactNode;
}

/** One accelerator family: direct inference, end to end, and how it was run. */
export function BenchmarkDetail({ platform, other, e2eExtra }: BenchmarkDetailProps) {
  // Two end-to-end headlines and the engine's best, so the stat row speaks for both levels.
  const e2e = summaryGains(platform.e2e);
  const stats = [e2e[0], e2e[1], summaryGains(platform.direct)[0]].filter((g) => g !== undefined);

  return (
    <div>
      <PageHeader
        kicker={`Benchmark · ${platform.name}`}
        title={<span>{platform.headline}</span>}
        lead={
          <>
            <p>{platform.lead}</p>
            <p className="mt-4 font-mono text-[12px] uppercase tracking-label text-ink-3">
              {platform.model} · {platform.accelerator}
            </p>
          </>
        }
        actions={
          <>
            <Link to="/benchmark" className="w-full sm:w-auto">
              <Button variant="secondary" size="lg" className="w-full sm:w-auto" leftIcon={<ArrowLeft className="h-4 w-4" />}>All benchmarks</Button>
            </Link>
            <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Reproduce it on your workload</Button>
            </a>
          </>
        }
      />

      <section className="border-b border-line bg-paper-2">
        <div className="mx-auto grid max-w-site grid-cols-1 gap-6 px-4 py-10 sm:grid-cols-3 sm:px-6">
          {stats.map((g, i) => (
            <Stat key={`${g.metric}-${g.vs}`} value={formatGain(g)} label={`${g.metric} — vs ${g.vs}`} tone={i === 0 ? 'accent' : 'ink'} />
          ))}
        </div>
      </section>

      <Section n="01" kicker={platform.direct.title} title={platform.direct.question} lead={platform.direct.lead}>
        <LevelResults level={platform.direct} />
      </Section>

      <Section n="02" kicker={platform.e2e.title} title={platform.e2e.question} lead={platform.e2e.lead} tone="white">
        <LevelResults level={platform.e2e} />
        {e2eExtra && <div className="mt-10 space-y-6">{e2eExtra}</div>}
      </Section>

      <Section n="03" kicker="How it was run" title="The test setup." lead="Each machine, what runs on it and how traffic flows between them.">
        <Card variant="raised" className="p-4 sm:p-8">
          <MermaidDiagram chart={platform.setup.diagram} label={platform.setup.diagramLabel} />
        </Card>
        <div className="mt-8">
          <RuledList items={platform.setup.parameters} />
        </div>
      </Section>

      <section className="border-t border-line">
        <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20">
          <Card variant="accent" className="p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile font-semibold tracking-heading md:text-section-desktop">The number that matters is the one on your workload.</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink-2">
              An assessment replays your recorded load on the Fnk stack against the one you run today, and reports the difference on your hardware.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a href={SITE.assessmentMailto} className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowUpRight className="h-4 w-4" />}>Request an assessment</Button>
              </a>
              <Link to={other.path} className="w-full sm:w-auto">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>{other.name} results</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
